/**
 * Hook para métricas RUM de rendimiento móvil (app_performance_events).
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export interface AppPerformanceMilestoneStat {
  milestone: string;
  samples: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  max_ms: number;
}

export interface AppPerformanceLaunchStats {
  sessions: number;
  feed_first_paint_p50_ms: number | null;
  feed_first_paint_p95_ms: number | null;
}

export interface AppPerformancePlatformStat {
  platform: string;
  sessions: number;
  react_first_render_p95_ms: number | null;
  feed_first_paint_p95_ms: number | null;
  auth_ready_p95_ms: number | null;
}

export interface AppPerformanceSlowDevice {
  device_model: string;
  platform: string;
  sessions: number;
  feed_first_paint_p95_ms: number;
}

export interface AppPerformanceSummary {
  period_days: number;
  total_sessions: number;
  slow_feed_sessions: number;
  slow_feed_threshold_ms: number;
  milestones: AppPerformanceMilestoneStat[];
  first_launch: AppPerformanceLaunchStats;
  returning: AppPerformanceLaunchStats;
  by_platform: AppPerformancePlatformStat[];
  slow_devices: AppPerformanceSlowDevice[];
}

export interface AppPerformanceSlowSession {
  session_id: string;
  user_id: string | null;
  display_name: string | null;
  email: string | null;
  platform: string | null;
  device_model: string | null;
  app_version: string | null;
  is_first_launch: boolean;
  feed_first_paint_ms: number;
  react_first_render_ms: number | null;
  auth_ready_ms: number | null;
  blank_screen_events: number;
  created_at: string;
}

export function useAppPerformanceSummary(days: number = 7) {
  return useQuery<AppPerformanceSummary>({
    queryKey: ['admin', 'app-performance-summary', days],
    queryFn: async () => {
      const { data, error } = await dbClient.rpc('get_app_performance_summary', {
        p_days: days,
      });
      if (error) throw error;
      const normalized = normalizeRpcResponse(data);
      const payload = Array.isArray(normalized) ? normalized[0] : normalized;
      return (payload ?? data) as AppPerformanceSummary;
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useAppPerformanceSlowSessions(days: number = 7, limit: number = 50) {
  return useQuery<AppPerformanceSlowSession[]>({
    queryKey: ['admin', 'app-performance-slow-sessions', days, limit],
    queryFn: async () => {
      const { data, error } = await dbClient.rpc('admin_get_app_performance_slow_sessions', {
        p_days: days,
        p_limit: limit,
      });
      if (error) throw error;
      return normalizeRpcResponse(data) as AppPerformanceSlowSession[];
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function formatPerfMs(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}
